export interface PlannerAutoAssignAssignment {
  id: string;
  service_plan_id: string;
  role_id?: string | null;
  user_id?: string | null;
  status?: string | null;
  created_at?: string | null;
}

export interface PlannerAutoAssignPlan {
  id: string;
  service_date: string;
  service_plan_assignments?: PlannerAutoAssignAssignment[] | null;
}

export interface PlannerAutoAssignMember {
  id: string;
  default_service_plan_role_id?: string | null;
  secondary_service_plan_role_ids?: string[] | null;
}

export interface PlannerAutoAssignAvailability {
  service_plan_id: string;
  user_id: string;
  availability_status: string;
}

export interface PlannerAutoAssignUnavailability {
  user_id: string;
  starts_at: string;
  ends_at: string;
}

export interface PlannerAutoAssignRuleMember {
  user_id: string;
  priority: number;
}

export interface PlannerAutoAssignRule {
  role_id: string;
  service_plan_id?: string | null;
  fallback_to_default?: boolean | null;
  rule_members?: PlannerAutoAssignRuleMember[] | null;
}

export interface PlannerAutoAssignDecision {
  role_id: string;
  action: 'keep' | 'assign' | 'unassign' | 'skip';
  user_id: string | null;
  assignment_id: string | null;
  skip_reason: string | null;
}

export interface PlannerAutoAssignUpdate {
  assignment_id: string;
  role_id: string;
  user_id: string | null;
  status: string;
}

export interface PlannerAutoAssignInsert {
  service_plan_id: string;
  role_id: string;
  user_id: string;
  status: string;
}

export interface PlannerAutoAssignComputation {
  decisions: PlannerAutoAssignDecision[];
  updates: PlannerAutoAssignUpdate[];
  inserts: PlannerAutoAssignInsert[];
}

interface AssignmentStats {
  countByUser: Map<string, number>;
  lastByUser: Map<string, number>;
}

type BlockReasonCode =
  | 'default_role'
  | 'calendar_unavailable'
  | 'service_unavailable'
  | 'one_service_per_day'
  | 'missing_member';

function parseTime(value: string): number {
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function overlapsServiceDate(
  serviceDateIso: string,
  rangeStartIso: string,
  rangeEndIso: string
): boolean {
  const serviceMs = parseTime(serviceDateIso);
  const startMs = parseTime(rangeStartIso);
  const endMs = parseTime(rangeEndIso);
  if (!serviceMs || !startMs || !endMs) return false;
  return startMs <= serviceMs && endMs > serviceMs;
}

function normalizeStatus(status?: string | null): string {
  return (status || '').trim().toLowerCase();
}

function getRolePriority(member: PlannerAutoAssignMember | undefined, roleId: string): 0 | 1 | null {
  if (!member) return null;
  if ((member.default_service_plan_role_id || null) === roleId) return 0;
  const secondaryRoleIds = new Set((member.secondary_service_plan_role_ids || []).filter(Boolean));
  if (secondaryRoleIds.has(roleId)) return 1;
  return null;
}

function buildRoleStats(plans: PlannerAutoAssignPlan[]): Map<string, AssignmentStats> {
  const statsByRole = new Map<string, AssignmentStats>();

  plans.forEach((plan) => {
    const planDateMs = parseTime(plan.service_date);
    (plan.service_plan_assignments ?? []).forEach((assignment) => {
      const roleId = assignment.role_id || null;
      const userId = assignment.user_id || null;
      if (!roleId || !userId) return;

      const stats = statsByRole.get(roleId) ?? {
        countByUser: new Map<string, number>(),
        lastByUser: new Map<string, number>(),
      };

      stats.countByUser.set(userId, (stats.countByUser.get(userId) ?? 0) + 1);
      if (planDateMs > (stats.lastByUser.get(userId) ?? 0)) {
        stats.lastByUser.set(userId, planDateMs);
      }

      statsByRole.set(roleId, stats);
    });
  });

  return statsByRole;
}

function summarizeBlockedReasons(counts: Map<BlockReasonCode, number>): string {
  if ((counts.get('default_role') ?? 0) > 0 && counts.size === 1) {
    return 'No member has this role set as primary or secondary.';
  }
  if ((counts.get('calendar_unavailable') ?? 0) > 0 && counts.size === 1) {
    return 'All matching members are unavailable in their calendar ranges.';
  }
  if ((counts.get('service_unavailable') ?? 0) > 0 && counts.size === 1) {
    return 'All matching members marked unavailable for this service.';
  }
  if ((counts.get('one_service_per_day') ?? 0) > 0 && counts.size === 1) {
    return 'All matching members are already assigned to another role for this service.';
  }
  if ((counts.get('missing_member') ?? 0) > 0 && counts.size === 1) {
    return 'No valid member profiles were found for this role.';
  }

  const parts: string[] = [];
  const calendarCount = counts.get('calendar_unavailable') ?? 0;
  const serviceCount = counts.get('service_unavailable') ?? 0;
  const onePerServiceCount = counts.get('one_service_per_day') ?? 0;
  const defaultRoleCount = counts.get('default_role') ?? 0;

  if (calendarCount > 0) parts.push(`${calendarCount} calendar blocked`);
  if (serviceCount > 0) parts.push(`${serviceCount} service unavailable`);
  if (onePerServiceCount > 0) parts.push(`${onePerServiceCount} assigned to another role for this service`);
  if (defaultRoleCount > 0) parts.push(`${defaultRoleCount} role mismatch`);

  if (!parts.length) {
    return 'No eligible member found for this role.';
  }
  return `No eligible member found (${parts.join(', ')}).`;
}

function summarizeRuleBlockedReasons(counts: Map<BlockReasonCode, number>): string {
  const baseReason = summarizeBlockedReasons(counts);
  if (!counts.size) {
    return 'No preferred members are configured for this role.';
  }
  return `Preferred members were not eligible. ${baseReason}`;
}

function getEffectiveRule(
  rules: PlannerAutoAssignRule[],
  roleId: string,
  servicePlanId: string
): PlannerAutoAssignRule | null {
  const serviceRule = rules.find(
    (rule) => rule.role_id === roleId && (rule.service_plan_id || null) === servicePlanId
  );
  if (serviceRule) return serviceRule;

  return rules.find(
    (rule) => rule.role_id === roleId && (rule.service_plan_id || null) === null
  ) ?? null;
}

function getOrderedRuleMembers(rule: PlannerAutoAssignRule | null): string[] {
  return [...(rule?.rule_members ?? [])]
    .filter((member) => !!member.user_id)
    .sort((a, b) => a.priority - b.priority || a.user_id.localeCompare(b.user_id))
    .map((member) => member.user_id);
}

export function computePlannerAutoAssign({
  servicePlanId,
  plans,
  requiredRoleIds,
  members,
  availability,
  unavailability,
  rules = [],
}: {
  servicePlanId: string;
  plans: PlannerAutoAssignPlan[];
  requiredRoleIds: string[];
  members: PlannerAutoAssignMember[];
  availability: PlannerAutoAssignAvailability[];
  unavailability: PlannerAutoAssignUnavailability[];
  rules?: PlannerAutoAssignRule[];
}): PlannerAutoAssignComputation {
  const targetPlan = plans.find((plan) => plan.id === servicePlanId);
  if (!targetPlan) {
    return { decisions: [], updates: [], inserts: [] };
  }

  const requiredRoleSet = new Set(requiredRoleIds.filter(Boolean));
  const orderedRoleIds = Array.from(requiredRoleSet).sort((a, b) => a.localeCompare(b));

  const memberById = new Map(members.map((member) => [member.id, member]));
  const memberIdsByRole = new Map<string, string[]>();
  members.forEach((member) => {
    const roleIds = new Set<string>();
    if (member.default_service_plan_role_id) roleIds.add(member.default_service_plan_role_id);
    (member.secondary_service_plan_role_ids || []).forEach((roleId) => {
      if (roleId) roleIds.add(roleId);
    });

    roleIds.forEach((roleId) => {
      const entries = memberIdsByRole.get(roleId) ?? [];
      entries.push(member.id);
      memberIdsByRole.set(roleId, entries);
    });
  });

  const serviceAvailabilityByUser = new Map<string, string>();
  availability.forEach((entry) => {
    if (entry.service_plan_id !== servicePlanId || !entry.user_id) return;
    serviceAvailabilityByUser.set(entry.user_id, normalizeStatus(entry.availability_status));
  });

  const calendarBlockedUserIds = new Set<string>();
  unavailability.forEach((entry) => {
    if (!entry.user_id) return;
    if (overlapsServiceDate(targetPlan.service_date, entry.starts_at, entry.ends_at)) {
      calendarBlockedUserIds.add(entry.user_id);
    }
  });

  const roleStatsByRoleId = buildRoleStats(plans);

  const targetAssignments = [...(targetPlan.service_plan_assignments ?? [])].sort((a, b) =>
    parseTime(a.created_at || '') - parseTime(b.created_at || '')
  );
  const firstAssignmentByRoleId = new Map<string, PlannerAutoAssignAssignment>();
  targetAssignments.forEach((assignment) => {
    if (!assignment.role_id) return;
    if (!firstAssignmentByRoleId.has(assignment.role_id)) {
      firstAssignmentByRoleId.set(assignment.role_id, assignment);
    }
  });

  const reservedUsers = new Map<string, string>();
  targetAssignments.forEach((assignment) => {
    if (!assignment.user_id) return;
    if (requiredRoleSet.has(assignment.role_id || '')) return;
    reservedUsers.set(assignment.user_id, assignment.role_id || 'existing');
  });

  const decisions: PlannerAutoAssignDecision[] = [];
  const updates: PlannerAutoAssignUpdate[] = [];
  const inserts: PlannerAutoAssignInsert[] = [];

  const evaluateMember = (
    userId: string,
    roleId: string,
    options?: { allowRuleOverride?: boolean }
  ): { allowed: boolean; code?: BlockReasonCode } => {
    const member = memberById.get(userId);
    if (!member) return { allowed: false, code: 'missing_member' };
    if (!options?.allowRuleOverride && getRolePriority(member, roleId) === null) {
      return { allowed: false, code: 'default_role' };
    }
    if (calendarBlockedUserIds.has(userId)) {
      return { allowed: false, code: 'calendar_unavailable' };
    }
    if (serviceAvailabilityByUser.get(userId) === 'unavailable') {
      return { allowed: false, code: 'service_unavailable' };
    }

    const reservedRole = reservedUsers.get(userId);
    if (reservedRole && reservedRole !== roleId) {
      return { allowed: false, code: 'one_service_per_day' };
    }

    return { allowed: true };
  };

  orderedRoleIds.forEach((roleId) => {
    const effectiveRule = getEffectiveRule(rules, roleId, servicePlanId);
    const orderedRuleMembers = getOrderedRuleMembers(effectiveRule);
    const existing = firstAssignmentByRoleId.get(roleId) ?? null;
    const existingUserId = existing?.user_id || null;

    const candidates = memberIdsByRole.get(roleId) ?? [];
    const blockedCounts = new Map<BlockReasonCode, number>();
    const eligibleCandidates: string[] = [];

    const ruleBlockedCounts = new Map<BlockReasonCode, number>();
    const eligibleRuleCandidates: string[] = [];

    orderedRuleMembers.forEach((candidateId) => {
      const eligibility = evaluateMember(candidateId, roleId, { allowRuleOverride: true });
      if (eligibility.allowed) {
        eligibleRuleCandidates.push(candidateId);
      } else if (eligibility.code) {
        ruleBlockedCounts.set(eligibility.code, (ruleBlockedCounts.get(eligibility.code) ?? 0) + 1);
      }
    });

    candidates.forEach((candidateId) => {
      const eligibility = evaluateMember(candidateId, roleId);
      if (eligibility.allowed) {
        eligibleCandidates.push(candidateId);
      } else if (eligibility.code) {
        blockedCounts.set(eligibility.code, (blockedCounts.get(eligibility.code) ?? 0) + 1);
      }
    });

    eligibleCandidates.sort((a, b) => {
      const aPriority = getRolePriority(memberById.get(a), roleId) ?? 99;
      const bPriority = getRolePriority(memberById.get(b), roleId) ?? 99;
      if (aPriority !== bPriority) return aPriority - bPriority;

      const stats = roleStatsByRoleId.get(roleId);
      const aCount = stats?.countByUser.get(a) ?? 0;
      const bCount = stats?.countByUser.get(b) ?? 0;
      if (aCount !== bCount) return aCount - bCount;

      const aLast = stats?.lastByUser.get(a) ?? 0;
      const bLast = stats?.lastByUser.get(b) ?? 0;
      if (aLast !== bLast) return aLast - bLast;

      return a.localeCompare(b);
    });

    const selectedUserId = eligibleRuleCandidates[0]
      ?? (
        effectiveRule && effectiveRule.fallback_to_default === false
          ? null
          : eligibleCandidates[0] ?? null
      );

    if (selectedUserId && existingUserId === selectedUserId) {
      const existingEligibility = evaluateMember(
        existingUserId,
        roleId,
        orderedRuleMembers.includes(existingUserId) ? { allowRuleOverride: true } : undefined
      );
      if (existingEligibility.allowed) {
        reservedUsers.set(existingUserId, roleId);
        decisions.push({
          role_id: roleId,
          action: 'keep',
          user_id: existingUserId,
          assignment_id: existing?.id ?? null,
          skip_reason: null,
        });
        return;
      }
    }

    if (selectedUserId) {
      reservedUsers.set(selectedUserId, roleId);

      if (existing?.id) {
        if (existing.user_id !== selectedUserId || normalizeStatus(existing.status) !== 'pending') {
          updates.push({
            assignment_id: existing.id,
            role_id: roleId,
            user_id: selectedUserId,
            status: 'pending',
          });
        }
      } else {
        inserts.push({
          service_plan_id: servicePlanId,
          role_id: roleId,
          user_id: selectedUserId,
          status: 'pending',
        });
      }

      decisions.push({
        role_id: roleId,
        action: 'assign',
        user_id: selectedUserId,
        assignment_id: existing?.id ?? null,
        skip_reason: null,
      });
      return;
    }

    const skipReason =
      effectiveRule && effectiveRule.fallback_to_default === false
        ? summarizeRuleBlockedReasons(ruleBlockedCounts)
        : summarizeBlockedReasons(blockedCounts);

    if (existing?.id && existing.user_id) {
      updates.push({
        assignment_id: existing.id,
        role_id: roleId,
        user_id: null,
        status: 'pending',
      });
      decisions.push({
        role_id: roleId,
        action: 'unassign',
        user_id: null,
        assignment_id: existing.id,
        skip_reason: skipReason,
      });
      return;
    }

    decisions.push({
      role_id: roleId,
      action: 'skip',
      user_id: null,
      assignment_id: existing?.id ?? null,
      skip_reason: skipReason,
    });
  });

  return { decisions, updates, inserts };
}